import React from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { TextField } from "../forms/textField";
import { SwitchField } from "../forms/switchField";
import { Article } from "../../models/responses/article";
import { ClientType } from "../../models/enums/clientTypeEnum";

interface ArticleModalProps {
  show: boolean;
  article: Article;
  clientType: ClientType;
  articleStockStatus: Record<string, any>;
  onHide: () => void;
}

const getStockStatusMessage = (status: any): string => {
  if (!status) return "";
  if (typeof status === "object") {
    if (status.error) return "Fel vid hämtning";
    if (status.message) return status.message;
    return JSON.stringify(status);
  }
  if (typeof status === "string") return status;
  return "";
};

const ArticleModal: React.FC<ArticleModalProps> = ({
  show,
  article,
  clientType,
  articleStockStatus,
  onHide,
}) => {
  const isFleet = clientType === ClientType.Fleet;
  const isStandard = clientType === ClientType.Standard;

  return (
    <Modal size="xl" show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {`${article.articleNumber} - ${article.description}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <TextField label="Beskrivning" value={article.description} colSize={6} />
          </Row>
          <Row>
            <TextField label="Antal beställda" value={article.quantity} colSize={2} type="number" />
            <TextField label="Antal levererade" value={article.deliveredQuantity} colSize={2} type="number" />
            {isFleet && (
              <>
                <TextField label="Lager från AX" value={article.stock ?? "Ej hämtat"} colSize={2} />
                <TextField
                  label="Meddelande från AX-export"
                  value={article.id ? (getStockStatusMessage(articleStockStatus[article.id]) ?? "") : ""}
                  colSize={6}
                />
              </>
            )}
          </Row>
          <Row>
            <TextField label="Leverantör" value={article.supplier?.name} colSize={6} />
            <TextField label="Artikelnummer" value={article.articleNumber} colSize={6} />
          </Row>
          <Row>
            <TextField label="Extern kommentar" value={article.comment} colSize={6} />
            <TextField label="Intern kommentar" value={article.internalComment} colSize={6} />
          </Row>
          <Row>
            <TextField label="Externt prefix" value={article.externalArticleCode} colSize={6} />
            {(isStandard || isFleet) && (
              <TextField label="Rabatt (%)" value={article.discount} colSize={6} type="number" />
            )}
          </Row>
          <Row>
            <TextField label="IO-nummer" value={article.purchaseOrderNumber} colSize={6} />
            <TextField label="Artikelnummer i AX" value={article.adId} colSize={6} />
          </Row>
          <Row>
            <TextField label="Inköpspris" value={article.purchasePrice} colSize={6} />
            <TextField label="Försäljningspris" value={article.salesPrice} colSize={6} />
          </Row>
          <Row>
            <TextField label="SPN" value={article.storeSalesPrice} colSize={6} />
            <TextField label="VSN" value={article.workshopSalesPrice} colSize={6} />
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>PL URL</Form.Label>
                <div className="mt-2">
                  {article.partUrl && article.partUrl.trim() !== "" ? (
                    <Link target="_blank" className="mt-1" to={article.partUrl}>
                      Länk
                    </Link>
                  ) : (
                    <span>-</span>
                  )}
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <SwitchField label="Exkluderad från order" checked={article.excludeFromWarehousePicking || false} />
          </Row>
          <hr />
          <Row>
            <TextField label="Totalt exkl. moms" value={article.totalExVat} colSize={4} />
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <div className="float-end d-inline-flex">
          <div className="me-3">
            <Form.Check
              type="switch"
              label="Granskad offert"
              className="mt-2"
              checked={article.reviewedQuote}
              disabled
              readOnly
            />
          </div>
          <div className="me-3 mt-2">
            {article.verified ? (
              <Badge bg="success">Klarmarkerad</Badge>
            ) : (
              <Badge bg="danger">Inte klarmarkerad</Badge>
            )}
          </div>
          <Button
            className="mb-3"
            variant="outline-primary"
            onClick={onHide}
          >
            Stäng
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ArticleModal;
